import { memo, Suspense, useState } from 'react'
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Radar as RadarIcon, Info } from 'lucide-react'
import type { RadarChartData } from '@/lib/jira/metrics'
import { getPerformanceLevel, getPerformanceLevelConfig } from '@/lib/jira/metrics'

interface JiraPerformanceRadarProps {
  data: RadarChartData[]
  title?: string
  height?: number
  loading?: boolean
  className?: string
  color?: string
  showAverage?: boolean
  compact?: boolean
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null

  const data = payload[0].payload
  const level = getPerformanceLevel(data.value)
  const levelConfig = getPerformanceLevelConfig(level)

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
        {data.axis}
      </p>
      <div className="flex items-center gap-2">
        <span className="text-lg font-bold text-gray-900 dark:text-white">
          {data.value}%
        </span>
        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${levelConfig.bgColor} ${levelConfig.color}`}>
          {levelConfig.label}
        </span>
      </div>
    </div>
  )
}

const ChartContent = memo(function ChartContent({
  data,
  height = 300,
  color = '#3b82f6',
}: Pick<JiraPerformanceRadarProps, 'data' | 'height' | 'color'>) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
        <PolarGrid
          gridType="polygon"
          className="stroke-gray-200 dark:stroke-gray-700"
        />
        <PolarAngleAxis
          dataKey="axis"
          tick={{ fontSize: 12, fill: 'currentColor' }}
          className="text-gray-600 dark:text-gray-400"
        />
        <PolarRadiusAxis
          angle={90}
          domain={[0, 100]}
          tick={{ fontSize: 10 }}
          className="text-gray-400 dark:text-gray-500"
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Radar
          name="Performance"
          dataKey="value"
          stroke={color}
          fill={color}
          fillOpacity={0.15}
          strokeWidth={2.5}
          dot={{ r: 4, fill: color, strokeWidth: 0 }}
          activeDot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
        />
      </RadarChart>
    </ResponsiveContainer>
  )
})

const PerformanceLegend = memo(function PerformanceLegend({ data }: { data: RadarChartData[] }) {
  const avgScore = Math.round(data.reduce((sum, d) => sum + d.value, 0) / data.length)
  const level = getPerformanceLevel(avgScore)
  const levelConfig = getPerformanceLevelConfig(level)

  return (
    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-4 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Ortalama Performans - Sol taraf */}
        <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
          <div className={`w-12 h-12 rounded-full ${levelConfig.bgColor} flex items-center justify-center flex-shrink-0`}>
            <span className="text-2xl">{levelConfig.icon}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">Ortalama Performans</p>
            <p className={`text-xl font-bold ${levelConfig.color} truncate`}>
              {avgScore}% - {levelConfig.label}
            </p>
          </div>
        </div>

        {/* Detaylı metrikler - Sağ taraf, küçük ekranlarda altına geçer */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-6 text-xs flex-1 sm:flex-initial sm:justify-end">
          {data.map((item) => (
            <div key={item.axis} className="flex items-center gap-1.5 flex-shrink-0">
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  item.value >= 75 ? 'bg-green-500' :
                  item.value >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
              />
              <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">{item.axis}</span>
              <span className="font-semibold text-gray-900 dark:text-white whitespace-nowrap">{item.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
})

const PerformanceMetricsList = memo(function PerformanceMetricsList({ data }: { data: RadarChartData[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
      {data.map((item) => {
        const level = getPerformanceLevel(item.value)
        const levelConfig = getPerformanceLevelConfig(level)

        return (
          <div
            key={item.axis}
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg min-w-0"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  item.value >= 75 ? 'bg-green-500' :
                  item.value >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{item.axis}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-bold text-gray-900 dark:text-white">{item.value}%</span>
              <span className={`text-xs ${levelConfig.color}`}>{levelConfig.icon}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
})

const LoadingState = memo(function LoadingState({ height }: { height: number }) {
  return (
    <div
      className="flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 rounded-lg animate-pulse"
      style={{ height }}
    >
      <RadarIcon className="h-16 w-16 text-gray-300 dark:text-gray-600" />
    </div>
  )
})

const JiraPerformanceRadar = memo(function JiraPerformanceRadar({
  data,
  title = 'Performans Analizi',
  height = 300,
  loading = false,
  className = '',
  color = '#3b82f6',
  showAverage = true,
  compact = false,
}: JiraPerformanceRadarProps) {
  const [showInfo, setShowInfo] = useState(false)

  return (
    <Card variant="default" padding="md" className={`overflow-hidden ${className}`}>
      <CardHeader compact>
        <CardTitle compact className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
              <RadarIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="truncate">{title}</span>
          </div>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0"
          >
            <Info className="h-4 w-4" />
          </button>
        </CardTitle>
        
        {showInfo && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Performans Alanları:</p>
            <ul className="space-y-0.5 text-blue-600 dark:text-blue-400">
              <li>• <strong>Completion:</strong> Görev tamamlama oranı</li>
              <li>• <strong>Velocity:</strong> Haftalık üretkenlik</li>
              <li>• <strong>Balance:</strong> İş yükü dağılımı</li>
              <li>• <strong>Risk-Free:</strong> Engellenmeyen görev oranı</li>
            </ul>
          </div>
        )}
      </CardHeader>
      
      <CardContent compact className="overflow-hidden">
        {loading ? (
          <LoadingState height={height} />
        ) : (
          <Suspense fallback={<LoadingState height={height} />}>
            <div className="overflow-hidden">
              <ChartContent data={data} height={height} color={color} />
            </div>
            {compact ? (
              <PerformanceMetricsList data={data} />
            ) : showAverage && (
              <PerformanceLegend data={data} />
            )}
          </Suspense>
        )}
      </CardContent>
    </Card>
  )
})

export default JiraPerformanceRadar

